import { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdPlayArrow,
  MdEventBusy,
  MdCheckCircle,
  MdCancel,
  MdSchedule,
  MdEventRepeat,
} from 'react-icons/md';
import Card from 'components/card/Card';
import AppointmentCompleteModal from 'components/layouts/patients/AppointmentCompleteModal';
import AppointmentCancelModal from 'components/layouts/patients/AppointmentCancelModal';
import AppointmentRescheduleModal from 'components/layouts/patients/AppointmentRescheduleModal';
import { useAppointments, useNoShowAppointment } from 'hooks/useAppointments';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { Appointment, EstadoCita, TipoCita } from 'types/models';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function todayRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const today = `${y}-${m}-${d}`;
  return { desde: today, hasta: today };
}

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function serviceLabel(appt: Appointment): string {
  if (appt.treatmentPlanId && appt.sessionInfo) {
    return `Plan: Ses. ${appt.sessionInfo.numeroSesion}/${appt.sessionInfo.totalSessions}`;
  }
  if (appt.treatmentPlanId) return 'Plan de tratamiento';
  switch (appt.tipoCita) {
    case TipoCita.PRIMERA_VEZ:   return 'Consulta normal';
    case TipoCita.SEGUIMIENTO:   return 'Seguimiento';
    case TipoCita.INTERCONSULTA: return 'Interconsulta';
    default:                     return appt.tipoCita;
  }
}

// ─── Status badge ─────────────────────────────────────────────────────────────

export const STATUS_CONFIG: Record<EstadoCita, { label: string; color: string; icon: any }> = {
  [EstadoCita.CONFIRMADA]:   { label: 'Confirmada',   color: 'teal',   icon: MdSchedule },
  [EstadoCita.COMPLETADA]:   { label: 'Completada',   color: 'green',  icon: MdCheckCircle },
  [EstadoCita.CANCELADA]:    { label: 'Cancelada',    color: 'red',    icon: MdCancel },
  [EstadoCita.NO_ASISTIO]:   { label: 'No asistió',  color: 'orange', icon: MdEventBusy },
  [EstadoCita.REPROGRAMADA]: { label: 'Reprogramada', color: 'gray',   icon: MdSchedule },
};

export function StatusBadge({ estado }: { estado: EstadoCita }) {
  const cfg = STATUS_CONFIG[estado] ?? { label: estado, color: 'gray', icon: MdSchedule };
  return (
    <Badge
      colorScheme={cfg.color}
      borderRadius='full'
      px='8px'
      py='2px'
      display='flex'
      alignItems='center'
      gap='4px'
      w='fit-content'
      fontSize='xs'
      flexShrink={0}
    >
      <Icon as={cfg.icon} w='10px' h='10px' />
      {cfg.label}
    </Badge>
  );
}

// ─── Action row ───────────────────────────────────────────────────────────────

type ModalType = 'complete' | 'reschedule' | 'cancel' | null;

export interface RowProps {
  appt: Appointment;
  onAction: (appt: Appointment, type: ModalType) => void;
}

export function AppointmentRow({ appt, onAction }: RowProps) {
  const noShow      = useNoShowAppointment();
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const isConfirmed = appt.estado === EstadoCita.CONFIRMADA;

  return (
    <Flex
      align='center'
      gap='10px'
      py='12px'
      borderBottom='1px solid'
      borderColor={borderColor}
      _last={{ borderBottom: 'none' }}
    >
      {/* Hora */}
      <Box flexShrink={0} w='46px'>
        <Text fontSize='sm' fontWeight='700' color={textColor}>
          {formatTime(appt.scheduledAt)}
        </Text>
        <Text fontSize='10px' color={mutedColor}>{appt.durationMinutes} min</Text>
      </Box>

      {/* Paciente */}
      <Flex align='center' gap='8px' flex={1} minW='0'>
        <Avatar
          size='sm'
          name={`${appt.patient.nombres} ${appt.patient.apellidos}`}
          bg='brand.500'
          color='white'
          flexShrink={0}
        />
        <Box minW='0'>
          <Text fontSize='sm' fontWeight='600' color={textColor} noOfLines={1}>
            {appt.patient.nombres} {appt.patient.apellidos}
          </Text>
          <Text fontSize='xs' color={mutedColor} noOfLines={1}>
            {serviceLabel(appt)}
          </Text>
        </Box>
      </Flex>

      {/* Estado */}
      <StatusBadge estado={appt.estado} />

      {/* Acciones */}
      {isConfirmed && (
        <Flex gap='2px' flexShrink={0}>
          <Tooltip label='Iniciar Atención' hasArrow placement='top'>
            <IconButton
              size='sm'
              aria-label='Iniciar Atención'
              icon={<Icon as={MdPlayArrow} w='16px' h='16px' />}
              colorScheme='brand'
              borderRadius='8px'
              onClick={() => onAction(appt, 'complete')}
            />
          </Tooltip>
          <Tooltip label='Reagendar' hasArrow placement='top'>
            <IconButton
              size='sm'
              aria-label='Reagendar'
              icon={<Icon as={MdEventRepeat} w='16px' h='16px' />}
              colorScheme='blue'
              variant='ghost'
              borderRadius='8px'
              onClick={() => onAction(appt, 'reschedule')}
            />
          </Tooltip>
          <Tooltip label='No asistió' hasArrow placement='top'>
            <IconButton
              size='sm'
              aria-label='No asistió'
              icon={<Icon as={MdEventBusy} w='16px' h='16px' />}
              colorScheme='orange'
              variant='ghost'
              borderRadius='8px'
              isLoading={noShow.isPending}
              onClick={() => noShow.mutate(appt.id)}
            />
          </Tooltip>
          <Tooltip label='Cancelar' hasArrow placement='top'>
            <IconButton
              size='sm'
              aria-label='Cancelar'
              icon={<Icon as={MdCancel} w='16px' h='16px' />}
              colorScheme='red'
              variant='ghost'
              borderRadius='8px'
              onClick={() => onAction(appt, 'cancel')}
            />
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
}

// ─── Appointment list (shared inner content) ──────────────────────────────────

interface ListProps {
  appointments: Appointment[];
  isLoading: boolean;
  isError: boolean;
  onAction: (appt: Appointment, type: ModalType) => void;
}

export function AppointmentListContent({ appointments, isLoading, isError, onAction }: ListProps) {
  const mutedColor      = useColorModeValue('gray.500', 'gray.400');
  const headerBorderColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  if (isLoading) {
    return (
      <Flex direction='column' gap='12px'>
        {[1, 2, 3].map(i => <Skeleton key={i} h='56px' borderRadius='8px' />)}
      </Flex>
    );
  }
  if (isError) {
    return <Text color='red.400' fontSize='sm'>Error al cargar la jornada. Recarga la página.</Text>;
  }
  if (appointments.length === 0) {
    return (
      <Flex direction='column' align='center' py='32px' gap='8px'>
        <Icon as={MdCheckCircle} w='36px' h='36px' color='green.300' />
        <Text color={mutedColor} fontSize='sm'>Sin citas programadas para hoy.</Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Flex
        display={{ base: 'none', md: 'flex' }}
        align='center'
        gap='10px'
        pb='8px'
        mb='4px'
        borderBottom='2px solid'
        borderColor={headerBorderColor}
      >
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' w='46px'>Hora</Text>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' flex={1}>Paciente / Servicio</Text>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase'>Estado</Text>
        <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' w='104px' textAlign='right'>Acción</Text>
      </Flex>
      {appointments.map(appt => (
        <AppointmentRow key={appt.id} appt={appt} onAction={onAction} />
      ))}
    </Box>
  );
}

// ─── Main widget ──────────────────────────────────────────────────────────────

interface Props {
  title?: string;
}

export default function TodayJornada({ title = 'Mi Jornada de Hoy' }: Props) {
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const { data: me } = useCurrentDbUser();
  const range = useMemo(todayRange, []);

  const { data, isLoading, isError } = useAppointments(
    me?.id ? { desde: range.desde, hasta: range.hasta, professionalId: me.id, limit: 50 } : {}
  );

  const appointments: Appointment[] = useMemo(() => {
    if (!data?.data) return [];
    return [...data.data].sort(
      (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );
  }, [data]);

  const [selected,   setSelected]   = useState<Appointment | null>(null);
  const [modalType,  setModalType]  = useState<ModalType>(null);

  const handleAction = (appt: Appointment, type: ModalType) => {
    setSelected(appt);
    setModalType(type);
  };

  const handleClose = () => {
    setSelected(null);
    setModalType(null);
  };

  const total      = appointments.length;
  const pendientes = appointments.filter(a => a.estado === EstadoCita.CONFIRMADA).length;

  return (
    <>
      <Card p='20px'>
        <Flex align='center' justify='space-between' mb='16px' flexWrap='wrap' gap='8px'>
          <Box>
            <Text color={textColor} fontSize='lg' fontWeight='700'>{title}</Text>
            {!isLoading && (
              <Text color={mutedColor} fontSize='xs'>
                {total} cita{total !== 1 ? 's' : ''} · {pendientes} pendiente{pendientes !== 1 ? 's' : ''}
              </Text>
            )}
          </Box>
          <Badge colorScheme='brand' borderRadius='full' px='12px' py='4px' fontSize='xs'>
            {new Date().toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'short' })}
          </Badge>
        </Flex>

        <AppointmentListContent
          appointments={appointments}
          isLoading={isLoading}
          isError={isError}
          onAction={handleAction}
        />
      </Card>

      {selected && modalType === 'complete' && (
        <AppointmentCompleteModal isOpen onClose={handleClose} appointment={selected} />
      )}
      {selected && modalType === 'reschedule' && (
        <AppointmentRescheduleModal isOpen onClose={handleClose} appointment={selected} />
      )}
      {selected && modalType === 'cancel' && (
        <AppointmentCancelModal isOpen onClose={handleClose} appointment={selected} />
      )}
    </>
  );
}
